import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { Search, RotateCcw, Download, Filter, Save, UsersRound } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import DatePickerInput from "../components/DatePickerInput";
import BranchMultiSelect from "../components/BranchMultiSelect";

const EMPTY = {
  q: "",
  branch_id: "",
  branch_ids: [],
  segment_id: "",
  points_min: "",
  points_max: "",
  spent_min: "",
  spent_max: "",
  created_from: "",
  created_to: "",
  last_activity_from: "",
  last_activity_to: "",
  dob_month: "",
  has_email: "",
  has_address: "",
  created_via: "",
  active: "",
  sort_by: "created_at",
  sort_dir: "desc",
};

const MONTHS = [
  { v: "1", l: "Gennaio" }, { v: "2", l: "Febbraio" }, { v: "3", l: "Marzo" },
  { v: "4", l: "Aprile" }, { v: "5", l: "Maggio" }, { v: "6", l: "Giugno" },
  { v: "7", l: "Luglio" }, { v: "8", l: "Agosto" }, { v: "9", l: "Settembre" },
  { v: "10", l: "Ottobre" }, { v: "11", l: "Novembre" }, { v: "12", l: "Dicembre" },
];

function buildBody(f) {
  const b = { limit: 500, offset: 0 };
  if (f.q) b.q = f.q;
  if (f.branch_id) b.branch_id = f.branch_id;
  if (f.branch_ids && f.branch_ids.length) b.branch_id = f.branch_ids.join(",");
  if (f.segment_id) b.segment_id = f.segment_id;
  if (f.points_min !== "") b.points_min = Number(f.points_min);
  if (f.points_max !== "") b.points_max = Number(f.points_max);
  if (f.spent_min !== "") b.spent_min = Number(f.spent_min);
  if (f.spent_max !== "") b.spent_max = Number(f.spent_max);
  if (f.created_from) b.created_from = f.created_from;
  if (f.created_to) b.created_to = f.created_to;
  if (f.last_activity_from) b.last_activity_from = f.last_activity_from;
  if (f.last_activity_to) b.last_activity_to = f.last_activity_to;
  if (f.dob_month) b.dob_month = Number(f.dob_month);
  if (f.has_email === "yes") b.has_email = true;
  else if (f.has_email === "no") b.has_email = false;
  if (f.has_address === "yes") b.has_address = true;
  else if (f.has_address === "no") b.has_address = false;
  if (f.created_via) b.created_via = f.created_via;
  if (f.active === "yes") b.active = true;
  else if (f.active === "no") b.active = false;
  b.sort_by = f.sort_by || "created_at";
  b.sort_dir = f.sort_dir || "desc";
  return b;
}

export default function AdvancedSearch() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [segments, setSegments] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [clusterOpen, setClusterOpen] = useState(false);
  const [clusterForm, setClusterForm] = useState({ name: "", description: "", color: "#F59E0B" });
  const [clusterSaving, setClusterSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/branches").then((r) => r.data).catch(() => []),
      api.get("/segments").then((r) => r.data).catch(() => []),
    ]).then(([b, s]) => {
      setBranches(b || []);
      setSegments(s || []);
    });
  }, []);

  const set = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const runSearch = async () => {
    setLoading(true);
    try {
      const body = buildBody(filters);
      const { data } = await api.post("/customers/search", body);
      setResults(data.results || []);
      setTotal(data.total || 0);
      setHasSearched(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFilters(EMPTY);
    setResults([]);
    setTotal(0);
    setHasSearched(false);
  };

  const exportCsv = () => {
    if (!results.length) {
      toast.error("Nessun risultato da esportare");
      return;
    }
    const rows = [
      ["Nome", "Carta", "Telefono", "Email", "Punti", "Speso", "Città", "Registrato", "Ultima attività"],
      ...results.map((r) => [
        r.name,
        r.card_number,
        r.phone,
        r.email || "",
        r.points,
        (r.total_spent || 0).toFixed(2),
        r.city || "",
        r.created_at ? r.created_at.slice(0, 10) : "",
        r.last_activity_at ? r.last_activity_at.slice(0, 10) : "",
      ]),
    ];
    const csv = rows.map((r) => r.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "ricerca_clienti.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) =>
    v !== "" && !["sort_by", "sort_dir"].includes(k)
  ).length;

  const saveAsCluster = async () => {
    if (!clusterForm.name.trim()) {
      toast.error("Dai un nome al cluster");
      return;
    }
    setClusterSaving(true);
    try {
      const filtersBody = buildBody(filters);
      const { data } = await api.post("/segments/from-search", {
        name: clusterForm.name.trim(),
        description: clusterForm.description.trim(),
        color: clusterForm.color,
        filters: filtersBody,
      });
      toast.success(`Cluster "${data.name}" creato con ${data.member_count} clienti`);
      setClusterOpen(false);
      setClusterForm({ name: "", description: "", color: "#F59E0B" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setClusterSaving(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-up" data-testid="advanced-search-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <span className="pill pill-red">RICERCA AVANZATA</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">
            Trova esattamente chi cerchi
          </h1>
          <p className="text-zinc-400 mt-1">
            Filtri multipli per esplorare la tua base clienti in profondità.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            data-testid="adv-save-cluster-button"
            variant="outline"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10 h-11"
            onClick={() => setClusterOpen(true)}
            disabled={!hasSearched || results.length === 0}
          >
            <UsersRound className="h-4 w-4 mr-2" /> Salva come cluster
          </Button>
          <Button
            data-testid="adv-reset-button"
            variant="outline"
            className="border-zinc-800 h-11"
            onClick={reset}
          >
            <RotateCcw className="h-4 w-4 mr-2" /> Azzera filtri
          </Button>
          <Button
            data-testid="adv-export-button"
            variant="outline"
            className="border-zinc-800 h-11"
            onClick={exportCsv}
            disabled={!results.length}
          >
            <Download className="h-4 w-4 mr-2" /> Esporta CSV
          </Button>
        </div>
      </div>

      {/* Filter Panel */}
      <div className="card-elev p-5" data-testid="adv-filter-panel">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-4 w-4 text-amber-300" />
          <h3 className="font-heading font-semibold">Filtri</h3>
          {activeFilterCount > 0 && (
            <span className="pill pill-gold text-[10px]">{activeFilterCount} attivi</span>
          )}
        </div>

        {/* Row 1: text + branch + segment + created_via */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Testo (nome/telefono/carta/email)</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
              <Input
                data-testid="adv-q"
                value={filters.q}
                onChange={(e) => set("q", e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && runSearch()}
                className="pl-9 bg-zinc-950 border-zinc-800"
                placeholder="Es. Mario, 3401234567…"
              />
            </div>
          </div>

          {user?.role === "admin" && (
            <div>
              <Label className="mb-1 inline-block text-zinc-300 text-xs">Sede</Label>
              <BranchMultiSelect
                branches={branches}
                value={filters.branch_ids || []}
                onChange={(ids) => set("branch_ids", ids)}
                testId="adv-branch"
              />
            </div>
          )}

          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Cluster</Label>
            <Select value={filters.segment_id || "__all__"} onValueChange={(v) => set("segment_id", v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="adv-segment" className="bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="Tutti" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Tutti i cluster</SelectItem>
                {segments.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Origine registrazione</Label>
            <Select value={filters.created_via || "__all__"} onValueChange={(v) => set("created_via", v === "__all__" ? "" : v)}>
              <SelectTrigger data-testid="adv-created-via" className="bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="Tutte" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">Qualsiasi</SelectItem>
                <SelectItem value="online">Online (portale)</SelectItem>
                <SelectItem value="counter">Al negozio</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Row 2: points and spent ranges */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Punti minimi</Label>
            <Input data-testid="adv-points-min" type="number" min="0" value={filters.points_min}
              onChange={(e) => set("points_min", e.target.value)} className="bg-zinc-950 border-zinc-800" />
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Punti massimi</Label>
            <Input data-testid="adv-points-max" type="number" min="0" value={filters.points_max}
              onChange={(e) => set("points_max", e.target.value)} className="bg-zinc-950 border-zinc-800" />
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Speso minimo €</Label>
            <Input data-testid="adv-spent-min" type="number" min="0" step="0.01" value={filters.spent_min}
              onChange={(e) => set("spent_min", e.target.value)} className="bg-zinc-950 border-zinc-800" />
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Speso massimo €</Label>
            <Input data-testid="adv-spent-max" type="number" min="0" step="0.01" value={filters.spent_max}
              onChange={(e) => set("spent_max", e.target.value)} className="bg-zinc-950 border-zinc-800" />
          </div>
        </div>

        {/* Row 3: dates */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Registrato dal</Label>
            <DatePickerInput testid="adv-created-from" value={filters.created_from}
              onChange={(e) => set("created_from", e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Registrato al</Label>
            <DatePickerInput testid="adv-created-to" value={filters.created_to}
              onChange={(e) => set("created_to", e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Ultima attività dal</Label>
            <DatePickerInput testid="adv-last-from" value={filters.last_activity_from}
              onChange={(e) => set("last_activity_from", e.target.value)} />
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Ultima attività al</Label>
            <DatePickerInput testid="adv-last-to" value={filters.last_activity_to}
              onChange={(e) => set("last_activity_to", e.target.value)} />
          </div>
        </div>

        {/* Row 4: flags + sort */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Mese compleanno</Label>
            <Select value={filters.dob_month || "__none__"} onValueChange={(v) => set("dob_month", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="adv-dob-month" className="bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Qualsiasi</SelectItem>
                {MONTHS.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Email presente</Label>
            <Select value={filters.has_email || "__any__"} onValueChange={(v) => set("has_email", v === "__any__" ? "" : v)}>
              <SelectTrigger data-testid="adv-has-email" className="bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Qualsiasi</SelectItem>
                <SelectItem value="yes">Solo con email</SelectItem>
                <SelectItem value="no">Solo senza email</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Indirizzo</Label>
            <Select value={filters.has_address || "__any__"} onValueChange={(v) => set("has_address", v === "__any__" ? "" : v)}>
              <SelectTrigger data-testid="adv-has-address" className="bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Qualsiasi</SelectItem>
                <SelectItem value="yes">Con indirizzo</SelectItem>
                <SelectItem value="no">Senza indirizzo</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="mb-1 inline-block text-zinc-300 text-xs">Stato</Label>
            <Select value={filters.active || "__any__"} onValueChange={(v) => set("active", v === "__any__" ? "" : v)}>
              <SelectTrigger data-testid="adv-active" className="bg-zinc-950 border-zinc-800">
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__any__">Qualsiasi</SelectItem>
                <SelectItem value="yes">Attivi</SelectItem>
                <SelectItem value="no">Disattivati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <Label className="mb-1 inline-block text-zinc-300 text-xs">Ordina per</Label>
              <Select value={filters.sort_by} onValueChange={(v) => set("sort_by", v)}>
                <SelectTrigger data-testid="adv-sort-by" className="bg-zinc-950 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at">Registrazione</SelectItem>
                  <SelectItem value="points">Punti</SelectItem>
                  <SelectItem value="total_spent">Speso</SelectItem>
                  <SelectItem value="name">Nome</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-24">
              <Label className="mb-1 inline-block text-zinc-300 text-xs">Dir</Label>
              <Select value={filters.sort_dir} onValueChange={(v) => set("sort_dir", v)}>
                <SelectTrigger data-testid="adv-sort-dir" className="bg-zinc-950 border-zinc-800">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="desc">↓</SelectItem>
                  <SelectItem value="asc">↑</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <Button
            data-testid="adv-search-button"
            onClick={runSearch}
            disabled={loading}
            className="bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] h-11 px-6"
          >
            <Search className="h-4 w-4 mr-2" />
            {loading ? "Ricerca…" : "Cerca clienti"}
          </Button>
        </div>
      </div>

      {/* Results */}
      <div className="card-elev p-4" data-testid="adv-results-panel">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-heading font-semibold text-lg">Risultati</h3>
            <p className="text-xs text-zinc-500">
              {hasSearched
                ? `${total} clienti trovati · ${results.length} mostrati`
                : "Imposta i filtri e premi Cerca."}
            </p>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
              <tr className="text-left">
                <th className="py-2 pr-3">Cliente</th>
                <th className="py-2 pr-3">Carta</th>
                <th className="py-2 pr-3">Telefono</th>
                <th className="py-2 pr-3">Punti</th>
                <th className="py-2 pr-3">Speso</th>
                <th className="py-2 pr-3">Città</th>
                <th className="py-2 pr-3">Registrato</th>
                <th className="py-2 pr-3">Ultima attività</th>
              </tr>
            </thead>
            <tbody>
              {!hasSearched && (
                <tr><td colSpan={8} className="py-8 text-center text-zinc-500">Nessuna ricerca ancora eseguita</td></tr>
              )}
              {hasSearched && results.length === 0 && (
                <tr><td colSpan={8} className="py-8 text-center text-zinc-500">Nessun cliente corrisponde ai filtri</td></tr>
              )}
              {results.map((c) => (
                <tr
                  key={c.id}
                  className="border-t border-zinc-900 hover:bg-zinc-900/50 cursor-pointer"
                  onClick={() => nav(`/clienti?open=${encodeURIComponent(c.id)}`)}
                  data-testid={`adv-row-${c.id}`}
                  title="Apri scheda cliente"
                >
                  <td className="py-3 pr-3">
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-zinc-500">{c.email || "—"}</div>
                  </td>
                  <td className="py-3 pr-3 font-mono text-xs">{c.card_number}</td>
                  <td className="py-3 pr-3">{c.phone}</td>
                  <td className="py-3 pr-3"><span className="pill pill-gold font-mono">{c.points}</span></td>
                  <td className="py-3 pr-3 text-zinc-400 font-mono">€ {c.total_spent?.toFixed?.(2) ?? "0.00"}</td>
                  <td className="py-3 pr-3 text-zinc-400">{c.city || "—"}</td>
                  <td className="py-3 pr-3 text-zinc-400 text-xs">{c.created_at ? c.created_at.slice(0, 10) : "—"}</td>
                  <td className="py-3 pr-3 text-zinc-400 text-xs">{c.last_activity_at ? c.last_activity_at.slice(0, 10) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {results.length >= 500 && (
          <div className="text-xs text-amber-300 mt-3 text-center">
            Mostrati i primi 500 risultati. Restringi i filtri per vedere gli altri.
          </div>
        )}
      </div>

      <Dialog open={clusterOpen} onOpenChange={setClusterOpen}>
        <DialogContent className="bg-[color:var(--bg-surface)] border-zinc-800">
          <DialogHeader><DialogTitle>Salva {total} clienti come cluster</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Nome cluster *</Label>
              <Input
                data-testid="save-cluster-name"
                value={clusterForm.name}
                onChange={(e) => setClusterForm({ ...clusterForm, name: e.target.value })}
                placeholder="Es. Clienti dormienti Milano"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Descrizione</Label>
              <Input
                data-testid="save-cluster-desc"
                value={clusterForm.description}
                onChange={(e) => setClusterForm({ ...clusterForm, description: e.target.value })}
                placeholder="Note interne (opzionale)"
                className="bg-zinc-950 border-zinc-800"
              />
            </div>
            <div>
              <Label className="text-zinc-300 mb-1 inline-block">Colore</Label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={clusterForm.color}
                  onChange={(e) => setClusterForm({ ...clusterForm, color: e.target.value })}
                  className="h-10 w-14 rounded-md border border-zinc-800 bg-transparent cursor-pointer"
                />
                <Input
                  value={clusterForm.color}
                  onChange={(e) => setClusterForm({ ...clusterForm, color: e.target.value })}
                  className="bg-zinc-950 border-zinc-800 font-mono text-xs"
                />
              </div>
            </div>
            <div className="text-xs text-zinc-500">
              Verranno salvati {total} clienti come cluster <strong>statico</strong>. Potrai poi
              collegarlo a una campagna dal pannello Campagne o Comunicazioni.
            </div>
            <Button
              data-testid="save-cluster-submit"
              onClick={saveAsCluster}
              disabled={clusterSaving}
              className="w-full bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)]"
            >
              <Save className="h-4 w-4 mr-2" /> {clusterSaving ? "Salvataggio…" : "Crea cluster"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
